import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControl,
  FormHelperText,
  Grid,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { CustomDatePicker } from "components/CustomDatePicker";
import { CustomTextField } from "components/CustomTextField";
import { ProductTypeChips } from "components/ProductTypeChips";
import { RegionSelect } from "components/RegionSelect";
import { DocsDrawer } from "docs/DocsDrawer";
import { Team } from "resource-planner-model";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { getJiraURL } from "utils/jiraURLService";
import * as yup from "yup";
import {
  AppState,
  ReportFeedbackAction,
  REPORT_APP_FEEDBACK
} from "../redux/AppTypes";
import {
  describeInvitationResults,
  hasInvitationWarning
} from "../utils/invitationStatus";
import { FETCH_TEAMS, PERSIST_TEAM } from "./TeamsTypes";
import { getTeamTypeLabel, useTeamTypes } from "./useTeamTypes";

const schema = yup.object().shape({
  harvest: yup.string().optional(),
  id: yup.string().optional(),
  jira: yup.string().optional(),
  lead: yup.string().required(),
  manager: yup.string().required(),
  name: yup.string().required(),
  productTypes: yup.array().of(yup.string()),
  type: yup.array().of(yup.string()),
});

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiFormControl-root": {
      margin: theme.spacing(1),
    },
    "& .MuiTextField-root": {
      margin: theme.spacing(0),
    },
  },
}));

export function TeamForm() {
  const history = useHistory();
  const classes = useStyles();
  const dispatch = useDispatch();
  const synching = useSelector((state: AppState) => state.teams.synching);
  const teams = useSelector((state: AppState) => state.teams.items);
  const params: { [key: string]: string } = useParams();
  const [team, setTeam] = useState({} as Team);
  const [loading, setLoading] = useState(true);
  const [invitationStatus, setInvitationStatus] = useState("");
  const form = useForm({ resolver: yupResolver(schema) });
  const selectedTeamTypeIds = (team.type || []).map((type) => type as string);
  const teamTypes = useTeamTypes(selectedTeamTypeIds);

  function onSubmit(data: any): void {
    const item: Team = {
      harvest: data.harvest,
      harvestHours: team.harvestHours || {},
      id: data.id,
      jira: data.jira,
      lead: data.lead,
      manager: (data.manager || "").trim().toLowerCase(),
      name: data.name,
      pagerDuty: "",
      productTypes: data.productTypes || [],
      offboardingDate: data.offboardingDate
        ? {
          year: data.offboardingDate?.getFullYear(),
          month: data.offboardingDate?.getMonth(),
          day: data.offboardingDate?.getDate(),
        }
        : undefined,
      region: data.region || undefined,
      type: team.type || [],
      teams: {
        enabled: false,
        webhook: "",
      },
    };
    const response = dispatch({
      type: PERSIST_TEAM,
      item,
    }) as any;
    response
      .then((result: any) => {
        const message = describeInvitationResults(result?.invitations);
        if (message) {
          setInvitationStatus(message);
          dispatch({
            type: REPORT_APP_FEEDBACK,
            feedback: message,
          } as ReportFeedbackAction);
        }
        if (!shouldCloseAfterTeamSave(result)) {
          return;
        }
        onClose();
      })
      .catch((error: any) => {
        const action: ReportFeedbackAction = {
          type: REPORT_APP_FEEDBACK,
          feedback: error.message || "",
        };
        dispatch(action);
      });
  }

  useEffect(() => {
    const id = params.id;
    let initialTeam = teams[id];
    if (!id) {
      setLoading(false);
    } else if (initialTeam) {
      setLoading(false);
      setTeam(initialTeam);
    } else {
      dispatch({ type: FETCH_TEAMS });
    }
  }, [dispatch, params.id, teams]);

  function onClose(): void {
    if (history.length > 0) {
      history.goBack();
    } else {
      history.push("/admninistration/teams");
    }
  }

  return (
    <Container maxWidth="md">
      {loading ? (
        <CircularProgress />
      ) : (
        <form
          className={classes.root}
          onSubmit={form.handleSubmit(onSubmit)}
          autoComplete="off"
        >
          <input
            type="hidden"
            name="id"
            value={params.id}
            ref={form.register}
          />
          <FormControl fullWidth>
            <Typography variant="h4">
              Team form <DocsDrawer path="/admin/teams.html" />
            </Typography>
          </FormControl>
          <FormControl fullWidth>
            <TextField
              name="name"
              variant="filled"
              label="Name"
              autoFocus
              defaultValue={team.name}
              error={!!form.errors.name}
              inputRef={form.register}
              helperText={form.errors.name?.message}
              inputProps={{
                form: {
                  autocomplete: "off",
                },
              }}
            />
          </FormControl>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <TextField
                  name="lead"
                  label="Lead"
                  defaultValue={team.lead}
                  error={!!form.errors.lead}
                  inputRef={form.register}
                  helperText={form.errors.lead?.message}
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <TextField
                  name="manager"
                  label="Manager"
                  defaultValue={team.manager}
                  error={!!form.errors.manager}
                  inputRef={form.register}
                  helperText={form.errors.manager?.message}
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <RegionSelect
                name="region"
                defaultValue={team.region}
                form={form}
              />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="type-label">Cloud</InputLabel>
                <Select
                  name="type"
                  multiple
                  value={selectedTeamTypeIds}
                  onChange={(e) => {
                    const value = e.target.value;
                    const typeIds = Array.isArray(value)
                      ? value
                      : String(value).split(",");
                    setTeam({ ...team, type: typeIds as Team["type"] });
                  }}
                  labelId="type-label"
                  error={!!form.errors.type}
                  inputRef={form.register}
                  input={<Input />}
                  renderValue={(selected) => {
                    const values = Array.isArray(selected) ? selected : [];
                    return values
                      .map((typeId) =>
                        getTeamTypeLabel(teamTypes, typeId as string)
                      )
                      .join(", ");
                  }}
                >
                  {teamTypes.map((type) => {
                    return (
                      <MenuItem key={type.id} value={type.id}>
                        <Checkbox
                          checked={selectedTeamTypeIds.indexOf(type.id) > -1}
                        />
                        <ListItemText primary={type.label} />
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>{form.errors.type?.message}</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <ProductTypeChips
                name="productTypes"
                defaultValue={team.productTypes || []}
                form={form}
              />
            </Grid>
            <Grid item xs={4}>
              <CustomTextField
                name="jira"
                label="JIRA Project Category id"
                defaultValue={team.jira || ""}
                form={form}
                disabled={!!team.jira}
                linkBaseUrl={
                  getJiraURL() + "/jira/projects?page=1&selectedCategory="
                }
                helperText="This is used to sync projects to the corresponding team board in Jira. IMPORTANT: DO NOT update with a URL or the team project key"
              />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <TextField
                  label="Harvest team URL"
                  name="harvest"
                  defaultValue={team.harvest}
                  error={!!form.errors.harvest}
                  inputRef={form.register}
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <CustomDatePicker
                year={team.offboardingDate?.year}
                month={team.offboardingDate?.month}
                day={team.offboardingDate?.day}
                control={form.control}
                record="offboardingDate"
                label="Offboarding date"
              />
            </Grid>
          </Grid>

          <Grid
            container
            direction="row"
            justifyContent="flex-end"
            alignItems="flex-start"
          >
            {invitationStatus ? (
              <Grid item xs={12}>
                <Typography color="textSecondary">{invitationStatus}</Typography>
              </Grid>
            ) : null}
            <FormControl margin="normal">
              <Button onClick={onClose} disabled={synching}>
                Discard
              </Button>
            </FormControl>
            <FormControl margin="normal">
              <Button
                disabled={synching}
                variant="contained"
                color="primary"
                type="submit"
              >
                Save
              </Button>
            </FormControl>
          </Grid>
        </form>
      )}
    </Container>
  );
}

export function shouldCloseAfterTeamSave(result: any): boolean {
  return !hasInvitationWarning(result?.invitations);
}
